import {
  LayoutGrid, MonitorSmartphone, AppWindow, Cloud, LifeBuoy, ShieldCheck,
  Blocks, Wrench, ArrowRight, ShieldCheck as Shield, Cpu, Activity, Headphones,
} from "lucide-react";

export const COMPANY = {
  name: "LKTH LTD",
  tagline: "Building Secure, Scalable Web Portals & IT Solutions",
  email: "Lateshkatravath3366@gmail.com",
  phoneDisplay: "07383 390922",
  phoneHref: "tel:+447383390922",
  addressLines: ["Unit 16 Park Village, Norfolk Park Road", "Sheffield, South Yorkshire", "S2 2UA, United Kingdom"],
  hours: "Monday – Friday, 9:00 – 17:30",
  mapEmbed:
    "https://maps.google.com/maps?q=Unit%2016%20Park%20Village%2C%20Norfolk%20Park%20Road%2C%20Sheffield%20S2%202UA%2C%20United%20Kingdom&t=&z=16&ie=UTF8&iwloc=&output=embed",
  mapLink:
    "https://www.google.com/maps/search/?api=1&query=Unit+16+Park+Village%2C+Norfolk+Park+Road%2C+Sheffield+S2+2UA",
};

export const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Case Studies", path: "/portfolio" },
  { label: "About Us", path: "/about" },
  { label: "Contact", path: "/contact" },
];

// Placeholder figures — edit to your real numbers.
export const STATS = [
  { value: 1000, suffix: "+", label: "Projects Delivered", testid: "stat-projects" },
  { value: 1500, suffix: "+", label: "Happy Clients", testid: "stat-clients" },
  { value: 2, suffix: "+", label: "Years of Experience", testid: "stat-experience" },
  { value: 100, suffix: "%", label: "UK-Based Support", testid: "stat-uk" },
];

export const TRUST_ITEMS = [
  { icon: Shield, label: "Secure" },
  { icon: Cpu, label: "Scalable" },
  { icon: Activity, label: "Reliable" },
  { icon: Headphones, label: "UK-Based Support" },
];

export const SERVICES = [
  {
    slug: "custom-web-portals",
    icon: LayoutGrid,
    title: "Custom Web Portal Development",
    short: "Customer, employee, vendor and admin portals that bring your whole operation into one secure platform.",
    description:
      "A well-built portal replaces spreadsheets, emails and manual admin with one secure, self-service platform. We design and build customer, employee, vendor and admin portals around the way your business actually works — with the right users seeing exactly the right information.",
    features: [
      "Customer, employee, vendor & admin portals",
      "Role-based access & secure authentication",
      "Document management & approval workflows",
      "Real-time dashboards & reporting",
      "Single sign-on (SSO) ready",
    ],
  },
  {
    slug: "website-design-development",
    icon: MonitorSmartphone,
    title: "Website Design & Development",
    short: "Fast, professional websites that build trust and turn visitors into enquiries.",
    description:
      "Your website is usually the first conversation a customer has with your business. We design and build fast, accessible, SEO-ready websites that present your company properly and convert visitors into enquiries — easy to update, and built to last.",
    features: [
      "Corporate & brochure websites",
      "E-commerce & booking websites",
      "Content management systems (CMS)",
      "SEO-ready, fast-loading builds",
      "Accessibility & mobile-first design",
    ],
  },
  {
    slug: "app-development",
    icon: AppWindow,
    title: "Web & Mobile Application Development",
    short: "Bespoke web and mobile apps engineered to streamline workflows and scale with you.",
    description:
      "When off-the-shelf software no longer fits, we build bespoke web and mobile applications that digitise your processes end to end — delivered in agile sprints, with regular demos so you always know where things stand.",
    features: [
      "React & modern JavaScript front ends",
      "Progressive web apps (PWA)",
      "Cross-platform mobile applications",
      "API-driven, scalable architecture",
      "Agile, sprint-based delivery",
    ],
  },
  {
    slug: "cloud-hosting",
    icon: Cloud,
    title: "Cloud Solutions & Hosting",
    short: "Reliable, cost-effective cloud infrastructure — migrated, hosted and managed for you.",
    description:
      "We move, host and manage business systems on proven cloud platforms, taking responsibility for uptime, security and backups so you never have to think about servers again — and only pay for what you use.",
    features: [
      "AWS & Microsoft Azure deployments",
      "Cloud migration & cost optimisation",
      "Scalable, secure managed hosting",
      "Automated backups & disaster recovery",
      "24/7 uptime monitoring",
    ],
  },
  {
    slug: "it-consulting-support",
    icon: LifeBuoy,
    title: "IT Consulting & Support",
    short: "Straight-talking IT guidance and a UK-based team that answers when you call.",
    description:
      "Technology decisions are easier with an honest partner. We help you plan, procure and maintain the right systems for your size and budget — then support them day to day with friendly, UK-based help.",
    features: [
      "IT strategy & technology roadmaps",
      "Systems audits & health checks",
      "UK-based helpdesk support",
      "Vendor & licence management",
      "Digital transformation advice",
    ],
  },
  {
    slug: "cybersecurity",
    icon: ShieldCheck,
    title: "Cybersecurity & Data Protection",
    short: "Security-first engineering that protects your data, your customers and your reputation.",
    description:
      "We build security in from day one and review it continuously — hardened authentication, encrypted data and UK GDPR-aligned processes — so you can demonstrate to customers and regulators that their information is safe with you.",
    features: [
      "Security assessments & hardening",
      "UK GDPR & data protection support",
      "Encryption & secure backups",
      "Firewall, anti-malware & monitoring",
      "Staff security awareness guidance",
    ],
  },
  {
    slug: "api-integrations",
    icon: Blocks,
    title: "API & Third-Party Integrations",
    short: "Connect your systems so data flows automatically — payments, CRM, accounting and more.",
    description:
      "Re-keying data between systems wastes hours and causes errors. We connect the tools you already use — payments, CRM, ERP, accounting, marketing — so information flows automatically and reliably.",
    features: [
      "Payment gateway integration",
      "CRM & ERP connectivity",
      "Custom API development",
      "Accounting & marketing tool integrations",
      "Automation of manual processes",
    ],
  },
  {
    slug: "managed-it",
    icon: Wrench,
    title: "Maintenance, Upgrades & Managed IT Support",
    short: "Proactive care that keeps your systems patched, performant and future-proof.",
    description:
      "Software ages quietly. Our managed support keeps your systems patched, backed up and performing — with agreed response times, monthly health reports and honest advice about when an upgrade is genuinely worth doing.",
    features: [
      "Scheduled maintenance & patching",
      "Performance monitoring & tuning",
      "Platform upgrades & modernisation",
      "Helpdesk with agreed response times",
      "Monthly system health reports",
    ],
  },
];

export const PROCESS = [
  { number: "01", name: "Discover", text: "We learn your goals, users and workflows, then map out the scope, risks and budget in plain English." },
  { number: "02", name: "Design", text: "Wireframes and clickable prototypes are refined with you until every screen feels right." },
  { number: "03", name: "Develop", text: "Clean, well-tested code is built in agile sprints, with regular demos as it comes together." },
  { number: "04", name: "Deploy", text: "We launch on hardened, monitored infrastructure — tested, secure and with no downtime." },
  { number: "05", name: "Support", text: "Ongoing monitoring, maintenance and improvements keep your platform fast and secure." },
];

export const WHY_CHOOSE = [
  { title: "Tailored Solutions", text: "Every build starts with your workflows, not a template. We scope precisely what your business needs — nothing more, nothing missing." },
  { title: "Security-First Engineering", text: "Security is designed in from day one: hardened authentication, encrypted data and UK GDPR-aligned practices." },
  { title: "Transparent Pricing", text: "Clear, fixed quotes and honest advice. You always know what you are paying for — and why." },
  { title: "Ongoing Support", text: "We do not disappear after launch. Real UK-based support, proactive monitoring and a long-term partnership." },
  { title: "Fast Delivery", text: "Agile, milestone-driven delivery means you see working software quickly — without cutting corners." },
];

// Sample content — replace with real client feedback.
export const TESTIMONIALS = [
  {
    quote: "LKTH took the time to really understand how our business works. The client portal they built has replaced endless spreadsheets and emails — our team and our customers both love it.",
    author: "Sarah M.",
    role: "Operations Director",
    org: "Professional Services Firm",
  },
  {
    quote: "From the first call, LKTH were clear about costs and realistic about timescales. The new booking system went live on schedule and the support since launch has been excellent.",
    author: "James T.",
    role: "Managing Director",
    org: "Hospitality Group",
  },
  {
    quote: "They migrated our systems to the cloud over a weekend with no downtime. Everything just works — and their monitoring seems to catch issues before we even notice them.",
    author: "Priya R.",
    role: "Finance Director",
    org: "Logistics Company",
  },
];

// Representative case studies — anonymised examples of platforms we build.
export const PROJECTS = [
  {
    title: "Meridian Client Portal",
    category: "Client Portal",
    summary: "A secure self-service portal where customers view documents, raise requests and track progress in real time.",
    stack: ["React", "Node.js", "MongoDB", "AWS"],
  },
  {
    title: "UrbanStay Booking System",
    category: "Booking Platform",
    summary: "Live availability, secure payments and automated confirmations for a holiday-let operator — replacing a manual diary.",
    stack: ["React", "Python", "PostgreSQL", "Stripe"],
  },
  {
    title: "Brightpath HR Portal",
    category: "HR Portal",
    summary: "Employee onboarding, leave requests and document signing handled in one place for a growing services company.",
    stack: ["React", "Node.js", "SQL"],
  },
  {
    title: "Halden Commerce",
    category: "E-commerce Platform",
    summary: "A fast online store with live stock sync, secure checkout and a simple admin dashboard for a busy retailer.",
    stack: ["React", "Node.js", "Stripe", "MongoDB"],
  },
  {
    title: "FleetLink Vendor Portal",
    category: "Vendor Management",
    summary: "Contractors upload compliance documents and jobs are allocated automatically across a UK logistics network.",
    stack: ["React", "Python", "Azure"],
  },
  {
    title: "VantageOps Dashboard",
    category: "Analytics Dashboard",
    summary: "A live operations dashboard pulling data from multiple systems into clear, exportable management reports.",
    stack: ["React", "Python", "AWS"],
  },
];

export const VALUES = [
  { title: "Craftsmanship", text: "We build software we are proud to put our name to — clean, documented and maintainable." },
  { title: "Security by Default", text: "We treat every system as if it were holding our own data." },
  { title: "Straight Talking", text: "Honest advice, clear pricing, no jargon and no surprises." },
  { title: "Long-Term Partnership", text: "We succeed when your systems keep working for years, not weeks." },
];

export const TECH = [
  "React", "Node.js", "Python", "TypeScript", "AWS", "Microsoft Azure",
  "MongoDB", "PostgreSQL / SQL", "Docker", "Kubernetes", "Tailwind CSS", "Linux",
];

export const SERVICE_OPTIONS = [
  "Custom Web Portal Development",
  "Website Design & Development",
  "Web & Mobile Application Development",
  "Cloud Solutions & Hosting",
  "IT Consulting & Support",
  "Cybersecurity & Data Protection",
  "API & Third-Party Integrations",
  "Maintenance & Managed IT Support",
  "Something else",
];

export { ArrowRight };
